import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBell } from 'react-icons/fa';

import { isEmployee } from '../Auth/permissions';
import { useNotifications } from './Notifications';
import { texteNotification } from './notificationTexte';

// Une demande déposée attend la RH : la supervision, déjà filtrée sur ce qui
// attend ; les notes de frais ont leur page.
const SUPERVISION = '/rh/supervision?etat=en_attente';
const DESTINATIONS_DEPOT = {
  conge: SUPERVISION,
  avance: SUPERVISION,
  acompte: SUPERVISION,
  pret: SUPERVISION,
  attestation: SUPERVISION,
  heures_sup: SUPERVISION,
  note_frais: '/rh/notes-frais',
};

// Une décision ou une remise concerne le salarié : la page de son espace.
const DESTINATIONS_SALARIE = {
  conge: '/espace/conges',
  avance: '/espace/avances',
  acompte: '/espace/avances',
  pret: '/espace/avances',
  note_frais: '/espace/notes-frais',
  attestation: '/espace/attestations',
  heures_sup: '/espace/pointage',
};

/**
 * Où mène une notification dans la coquille courante, ou `null`. La coquille
 * interne n'a pas de page « espace » : un compte interne qui a déposé sa
 * propre demande y lit la décision sans lien, plutôt que d'atterrir sur une
 * page introuvable.
 */
export const destination = (notification, salarie) => {
  // Yros/Kavero#468 : une demande de l'équipe attend le manager, dans son espace.
  if (notification.type === 'demande_a_valider') return salarie ? '/espace/equipe' : null;
  if (notification.type === 'demande_deposee' || notification.type === 'demande_validee_manager') {
    return salarie ? null : DESTINATIONS_DEPOT[notification.demande_type] || null;
  }
  return salarie ? DESTINATIONS_SALARIE[notification.demande_type] || null : null;
};

/**
 * La cloche de notifications persistantes (Yros/Kavero#21), pour tout compte
 * connecté : dans la barre du haut des comptes internes et dans l'en-tête de
 * l'espace du salarié.
 */
const Cloche = ({ user }) => {
  const { t, i18n } = useTranslation();
  const contexte = useNotifications();
  const { pathname } = useLocation();
  const [ouverte, setOuverte] = useState(false);
  const idPanneau = useId();
  const racine = useRef(null);
  const charger = contexte?.charger;

  useEffect(() => {
    setOuverte(false);
  }, [pathname]);

  useEffect(() => {
    if (!ouverte) return undefined;
    charger();
    const fermerSurEchap = (evenement) => {
      if (evenement.key === 'Escape') setOuverte(false);
    };
    const fermerAuClicDehors = (evenement) => {
      if (racine.current && !racine.current.contains(evenement.target)) setOuverte(false);
    };
    document.addEventListener('keydown', fermerSurEchap);
    document.addEventListener('mousedown', fermerAuClicDehors);
    return () => {
      document.removeEventListener('keydown', fermerSurEchap);
      document.removeEventListener('mousedown', fermerAuClicDehors);
    };
  }, [ouverte, charger]);

  if (!contexte) return null;

  const { nonLues, notifications, marquerLues } = contexte;
  const salarie = isEmployee(user);

  const suivre = (notification) => {
    if (!notification.lu) marquerLues({ ids: [notification.id] });
    setOuverte(false);
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString(i18n.language, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });

  const classeLigne = 'flex min-h-[44px] w-full items-start gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-[--bg-color]';

  return (
    <div ref={racine} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOuverte((etait) => !etait)}
        aria-expanded={ouverte}
        aria-controls={idPanneau}
        aria-label={nonLues > 0 ? t('cloche.boutonAvecTotal', { count: nonLues }) : t('cloche.titre')}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:border-[--color-primary] hover:text-[--color-primary] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--color-primary]"
      >
        <FaBell aria-hidden="true" className="h-4 w-4" />
        {nonLues > 0 && (
          <span aria-hidden="true" className="absolute -right-1 -top-1 min-w-[20px] rounded-full bg-orange-500 px-1.5 text-center text-[11px] font-bold leading-5 text-white">
            {nonLues > 99 ? '99+' : nonLues}
          </span>
        )}
      </button>

      {ouverte && (
        <div
          id={idPanneau}
          role="region"
          aria-label={t('cloche.titre')}
          className="absolute right-0 top-12 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-100 bg-white p-2 shadow-xl"
        >
          {nonLues > 0 && (
            <div className="flex justify-end px-2 pb-1">
              <button
                type="button"
                onClick={() => marquerLues({ toutes: true })}
                className="text-xs font-medium text-[--color-primary] hover:underline"
              >
                {t('cloche.toutMarquerLu')}
              </button>
            </div>
          )}
          {notifications.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500">{t('cloche.rien')}</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {notifications.map((notification) => {
                const lien = destination(notification, salarie);
                const { titre, corps } = texteNotification(notification, t);
                const contenu = (
                  <>
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.lu ? 'bg-transparent' : 'bg-orange-500'}`}
                    />
                    <span className="min-w-0 flex-1">
                      {!notification.lu && <span className="sr-only">{t('cloche.nonLue')}</span>}
                      <span className={`block ${notification.lu ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>
                        {titre}
                      </span>
                      {corps && (
                        <span className="block text-gray-500">{corps}</span>
                      )}
                      <time dateTime={notification.date} className="block text-xs text-gray-400">
                        {formatDate(notification.date)}
                      </time>
                    </span>
                  </>
                );
                return (
                  <li key={notification.id}>
                    {lien ? (
                      <Link to={lien} onClick={() => suivre(notification)} className={classeLigne}>
                        {contenu}
                      </Link>
                    ) : (
                      <button type="button" onClick={() => suivre(notification)} className={classeLigne}>
                        {contenu}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default Cloche;
